// Typed wrapper around GET /api/runs/{id}/frf_summary, the per-run FRF
// (frequency-response-function) advisory summary the backend writes for 7-post
// (non-replay) runs. The endpoint always returns HTTP 200: either the artifact
// JSON or an {available:false, reason, run_status} envelope (replay runs, or a
// run that has not produced the artifact yet). Every numeric leaf is
// NaN-omitted server-side, so the schema marks each one optional and nullable
// and the panel treats a missing leaf as "no value" rather than an error.
import { z } from "zod";

// Bands, low to high. lo carries the body modes (coherence is typically low
// there, ~0.35, so its numbers are advisory only); mid is the hub-control band;
// hi is the high-frequency band. Ranges are hyphenated for unambiguous display.
export const FRF_BANDS = ["lo", "mid", "hi"] as const;
export type FrfBandKey = (typeof FRF_BANDS)[number];

export const FRF_BAND_LABEL: Record<FrfBandKey, string> = {
  lo: "0.5-4 Hz",
  mid: "4-12 Hz",
  hi: "12-30 Hz",
};

export const FRF_CORNERS = ["lf", "rf", "lr", "rr"] as const;
export type FrfCornerKey = (typeof FRF_CORNERS)[number];

export const FRF_CORNER_LABEL: Record<FrfCornerKey, string> = {
  lf: "LF",
  rf: "RF",
  lr: "LR",
  rr: "RR",
};

// A single tolerant numeric leaf: absent (NaN-omitted) or JSON-null both map to
// undefined/null so any leaf may be missing without failing the whole parse.
const NumLeaf = z.number().nullish();

// Fleet-level (all-corner) band summary. Magnitude bias in dB (sim vs recorded;
// 0 is perfect), phase error in degrees, coherence 0..1.
const FrfBandSummarySchema = z.object({
  mag_db_absmean: NumLeaf,
  phase_deg_absmean: NumLeaf,
  coherence_mean: NumLeaf,
});
export type FrfBandSummary = z.infer<typeof FrfBandSummarySchema>;

const FrfBandsSchema = z.object({
  lo: FrfBandSummarySchema.nullish(),
  mid: FrfBandSummarySchema.nullish(),
  hi: FrfBandSummarySchema.nullish(),
});

// Per-corner: each quantity is a {lo, mid, hi} triplet of scalars.
const FrfBandTripletSchema = z.object({
  lo: NumLeaf,
  mid: NumLeaf,
  hi: NumLeaf,
});
export type FrfBandTriplet = z.infer<typeof FrfBandTripletSchema>;

const FrfPerCornerSchema = z.object({
  mag_db: FrfBandTripletSchema.nullish(),
  phase_deg: FrfBandTripletSchema.nullish(),
  coherence: FrfBandTripletSchema.nullish(),
});
export type FrfPerCorner = z.infer<typeof FrfPerCornerSchema>;

const FrfPerCornersSchema = z.object({
  lf: FrfPerCornerSchema.nullish(),
  rf: FrfPerCornerSchema.nullish(),
  lr: FrfPerCornerSchema.nullish(),
  rr: FrfPerCornerSchema.nullish(),
});

// The artifact carries no `available` flag on the wire; the presence of the
// bands/per_corner payload is the signal. schema_version tolerates number or
// string so a version bump can't fail the parse.
export const FrfArtifactSchema = z.object({
  schema_version: z.union([z.number(), z.string()]).nullish(),
  bands: FrfBandsSchema.nullish(),
  per_corner: FrfPerCornersSchema.nullish(),
  advisory_note: z.string().nullish(),
});
export type FrfArtifact = z.infer<typeof FrfArtifactSchema>;

// The unavailable envelope (replay runs, or a run without the artifact).
export const FrfUnavailableSchema = z.object({
  available: z.literal(false),
  reason: z.string(),
  run_status: z.string().nullish(),
});

// Normalised discriminated result: `available` is synthesised true for the
// artifact branch so the panel can switch on one flag.
export type FrfSummary =
  | ({ available: true } & FrfArtifact)
  | { available: false; reason: string; run_status: string | null };

export async function fetchFrfSummary(
  runId: number,
  opts: { signal?: AbortSignal } = {},
): Promise<FrfSummary> {
  const r = await fetch(`/api/runs/${runId}/frf_summary`, {
    signal: opts.signal,
  });
  // The endpoint is documented as always 200; guard non-2xx defensively so a
  // proxy error surfaces as a thrown fetch rather than a malformed parse.
  if (!r.ok) {
    throw new Error(`GET /api/runs/${runId}/frf_summary failed: ${r.status}`);
  }
  const raw: unknown = await r.json();
  // Discriminate on the available:false envelope; the artifact branch has no
  // discriminant field of its own.
  if (
    raw !== null &&
    typeof raw === "object" &&
    (raw as { available?: unknown }).available === false
  ) {
    const u = FrfUnavailableSchema.parse(raw);
    return {
      available: false,
      reason: u.reason,
      run_status: u.run_status ?? null,
    };
  }
  const a = FrfArtifactSchema.parse(raw);
  return { available: true, ...a };
}
